"use client";

import { memo, useCallback } from "react";

import { Field, FieldError } from "@/components/ui/field";
import { FlightFormHelper } from "@/feature/flight/search/lib/form";
import {
  CABIN_TYPES as tripsCabin,
  TRIP_TYPES as tripTypes,
} from "@/feature/flight/search/types/constants";
import {
  type TFlightSearchFormData,
  type TPassengerCounts,
} from "@/feature/flight/search/types/search";
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
  useWatch,
} from "react-hook-form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Button } from "@/components/ui/button";
import { AirportSelect } from "@/feature/flight/search/components/form/airport-select";
import { DatePicker } from "@/feature/flight/search/components/form/flight-date-picker";
import PassengerSelect from "@/feature/flight/search/components/form/passenger-select";
import { isRegularFlightFareType } from "@/feature/flight/search/lib/passenger-types";
import { TAirport } from "@/feature/flight/search/types/search";
import { cn } from "@/lib/utils";

interface AirportProps {
  form: UseFormReturn<TFlightSearchFormData>;
  name: `flights.${number}.from` | `flights.${number}.to`;
  label: string;
  placeholder: string;
}
const Airport = memo(function Airport({
  form,
  name,
  label,
  placeholder,
}: AirportProps) {
  const handleAirportChange = useCallback(
    (
      field: ControllerRenderProps<TFlightSearchFormData>,
      airport: TAirport,
    ) => {
      const emptyAirport: TAirport = {
        iata_code: "",
        name: "",
        city_name: "",
        country: "",
      };
      const tripType = form.getValues("tripType");

      const parts = name.split(".");
      if (parts.length === 3) {
        const index = Number(parts[1]);
        const type = parts[2] as "from" | "to";
        const counterpart = type === "from" ? "to" : "from";
        const counterpartPath = `flights.${index}.${counterpart}` as const;
        const counterpartValue = form.getValues(
          counterpartPath,
        ) as unknown as TAirport;

        if (
          airport.iata_code &&
          counterpartValue?.iata_code &&
          airport.iata_code === counterpartValue.iata_code
        ) {
          // Clear the changed field to avoid a hidden invalid route being submitted/serialized.
          field.onChange(emptyAirport);

          form.setError(name, {
            type: "manual",
            message: type === "from" ? "Same as destination" : "Same as origin",
          });

          if (tripType === "roundtrip" && name.startsWith("flights.0.")) {
            if (name === "flights.0.from") {
              form.setValue("flights.1.to", emptyAirport, {
                shouldDirty: true,
                shouldTouch: true,
              });
            } else if (name === "flights.0.to") {
              form.setValue("flights.1.from", emptyAirport, {
                shouldDirty: true,
                shouldTouch: true,
              });
            }
          }
          return;
        }

        field.onChange(airport);
        if (airport.iata_code) {
          form.clearErrors(name);
        }

        // Multi-city helper: copy destination -> next origin (only if next origin is empty).
        if (tripType === "multicity" && type === "to") {
          const flights = form.getValues("flights");
          const nextIndex = index + 1;
          if (nextIndex < flights.length) {
            const nextFromPath = `flights.${nextIndex}.from` as const;
            const nextFromValue = form.getValues(
              nextFromPath,
            ) as unknown as TAirport;
            if (!nextFromValue?.iata_code) {
              form.setValue(nextFromPath, airport, {
                shouldDirty: true,
                shouldTouch: true,
              });
              form.clearErrors(nextFromPath);
            }
          }
        }
      } else {
        field.onChange(airport);
        if (airport.iata_code) {
          form.clearErrors(name);
        }
      }

      if (tripType === "roundtrip" && name.startsWith("flights.0.")) {
        if (name === "flights.0.from" && airport) {
          form.setValue("flights.1.to", airport, {
            shouldDirty: true,
            shouldTouch: true,
          });
        } else if (name === "flights.0.to" && airport) {
          form.setValue("flights.1.from", airport, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      }
    },
    [form, name],
  );

  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className="">
          {/* <FieldLabel>
            {fieldState.invalid ? (
              <FieldError errors={[fieldState.error]} />
            ) : (
              label
            )}
          </FieldLabel> */}
          <AirportSelect
            value={field.value}
            onChange={(airport) => handleAirportChange(field, airport)}
            label={label}
            placeholder={placeholder}
            isError={fieldState.invalid}
          />
        </Field>
      )}
    />
  );
});

interface FlightDateFieldProps {
  form: UseFormReturn<TFlightSearchFormData>;
  name: `flights.${number}.departDate`;
  label: string;
  placeholder?: string;
  disabledDate?: (date: Date) => boolean;
  onDateChange?: (date: Date) => void;
  minDate?: Date | null;
}
const Date = memo(function FlightDateField({
  form,
  name,
  label,
  placeholder,
  disabledDate,
  onDateChange,
  minDate,
}: FlightDateFieldProps) {
  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field, fieldState }) => {
        const handleChange = (date: Date | null | undefined) => {
          field.onChange(date);
          if (onDateChange && date) {
            onDateChange(date);
          }
          form.clearErrors(name);
        };
        return (
          <Field data-invalid={fieldState.invalid} className="">
            {/* <FieldLabel>
              {fieldState.invalid ? (
                <FieldError errors={[fieldState.error]} />
              ) : (
                label
              )}
            </FieldLabel> */}
            <DatePicker
              value={field.value}
              onChange={handleChange}
              label={label}
              placeholder={placeholder}
              disabledDate={disabledDate}
              isError={fieldState.invalid}
              minDate={minDate ?? undefined}
            />
          </Field>
        );
      }}
    />
  );
});

//! cabin class selector component
interface CabinClassProps {
  form: UseFormReturn<TFlightSearchFormData>;
}
const CabinClass = memo(function CabinClass({ form }: CabinClassProps) {
  return (
    <Controller
      control={form.control}
      name="cabin"
      render={({ field, fieldState }) => {
        return (
          <Field
            data-invalid={fieldState.invalid}
            className="w-[74px] min-w-0 md:w-fit"
          >
            <Select onValueChange={field.onChange} value={field.value}>
              <SelectTrigger
                className="h-8! w-full min-w-0 truncate rounded-lg border border-border/60 bg-background/85 px-2 text-xs font-semibold text-foreground shadow-xs hover:bg-accent/60 data-placeholder:text-muted-foreground md:rounded-md md:border-0 md:bg-primary/10 md:px-4 md:text-sm md:text-primary md:shadow-none md:data-placeholder:text-primary/80"
                aria-invalid={fieldState.invalid}
                aria-label="Cabin class"
              >
                <SelectValue placeholder="Select cabin class" />
              </SelectTrigger>
              <SelectContent>
                {tripsCabin.map((cabin) => (
                  <SelectItem key={cabin.value} value={String(cabin.value)}>
                    {cabin.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError errors={[fieldState.error]} />
          </Field>
        );
      }}
    />
  );
});
//* end cabin class selector component

//! passenger counter component
interface PassengerSelectorProps {
  form: UseFormReturn<TFlightSearchFormData>;
}
const Passenger = memo(function PassengerSelector({
  form,
}: PassengerSelectorProps) {
  const selectedFare = useWatch({
    control: form.control,
    name: "fareType",
  });
  const isRegularFare = isRegularFlightFareType(selectedFare);

  const handlePassengerChange = useCallback(
    (
      field: ControllerRenderProps<TFlightSearchFormData, "passengers">,
      newPassengers: TPassengerCounts,
    ) => {
      const currentChildrenAges = form.getValues("childrenAges") || [];
      const oldChildrenCount = field.value.children;
      const newChildrenCount = newPassengers.children;

      field.onChange(newPassengers);

      if (!isRegularFare) {
        if (currentChildrenAges.length > 0) {
          form.setValue("childrenAges", [], { shouldValidate: false });
          currentChildrenAges.forEach((_, index) => {
            form.clearErrors(`childrenAges.${index}`);
          });
        }
        return;
      }

      if (newChildrenCount !== oldChildrenCount) {
        if (newChildrenCount > currentChildrenAges.length) {
          const newAges: (number | undefined)[] = [...currentChildrenAges];
          for (let i = currentChildrenAges.length; i < newChildrenCount; i++) {
            newAges.push(undefined);
          }
          form.setValue("childrenAges", newAges, { shouldValidate: false });
        } else if (newChildrenCount < currentChildrenAges.length) {
          const newAges = currentChildrenAges.slice(0, newChildrenCount);
          form.setValue("childrenAges", newAges, { shouldValidate: false });
          for (let i = newChildrenCount; i < currentChildrenAges.length; i++) {
            form.clearErrors(`childrenAges.${i}`);
          }
        }
      }
    },
    [form, isRegularFare],
  );

  return (
    <Controller
      control={form.control}
      name="passengers"
      render={({ field, fieldState }) => {
        const childrenAgesErrors = form.formState.errors.childrenAges;
        const hasChildrenAgesError = Boolean(
          childrenAgesErrors &&
          (Array.isArray(childrenAgesErrors)
            ? childrenAgesErrors.some((err) => err)
            : childrenAgesErrors),
        );
        const hasAnyError = fieldState.invalid || hasChildrenAgesError;
        return (
          <Field
            data-invalid={hasAnyError}
            className="w-[78px] min-w-0 md:w-fit"
          >
            <PassengerSelect
              form={form}
              passengers={field.value}
              onChange={(passengers) =>
                handlePassengerChange(field, passengers)
              }
              withChildAges
              isError={hasAnyError}
              totalOnly={!isRegularFare}
            >
              <ChildrenAges form={form} passengers={field.value} />
            </PassengerSelect>
            <FieldError errors={[fieldState.error]} />
            {/* {hasChildrenAgesError && !fieldState.error && (
              <p className="text-sm text-destructive">
                Please provide age for all children
              </p>
            )} */}
          </Field>
        );
      }}
    />
  );
});
//* end passenger counter component

//! children ages selector component
interface ChildrenAgesProps {
  form: UseFormReturn<TFlightSearchFormData>;
  passengers: TPassengerCounts;
}
const ChildrenAges = memo(function ChildrenAges({
  form,
  passengers,
}: ChildrenAgesProps) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {Array.from({ length: passengers.children }, (_, index) => (
        <Controller
          key={`child-${index}`}
          control={form.control}
          name={`childrenAges.${index}`}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <Select
                name={`childrenAges.${index}`}
                onValueChange={(value) => {
                  const nextAge = Number(value);
                  const nextChildrenAges = [
                    ...(form.getValues("childrenAges") || []),
                  ];

                  nextChildrenAges[index] = nextAge;

                  form.setValue(`childrenAges.${index}`, nextAge, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                  form.clearErrors(`childrenAges.${index}`);

                  const requiredChildrenAges = nextChildrenAges.slice(
                    0,
                    passengers.children,
                  );

                  if (
                    requiredChildrenAges.length === passengers.children &&
                    requiredChildrenAges.every(
                      (age) => age !== undefined && age !== null,
                    )
                  ) {
                    form.clearErrors("childrenAges");
                  }
                }}
                value={field.value?.toString()}
              >
                <SelectTrigger
                  id={`child-age-${index}`}
                  aria-invalid={fieldState.invalid}
                  className="h-8 text-sm"
                >
                  <SelectValue placeholder={`Child ${index + 1}`} />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 10 }, (_, i) => i + 2).map((age) => (
                    <SelectItem key={age} value={age.toString()}>
                      {age} years
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        />
      ))}
    </div>
  );
});
//* end children ages selector component

//! trip type selector component
interface TripTypeSelectorProps {
  form: UseFormReturn<TFlightSearchFormData>;
}

type TripTypeValue = (typeof tripTypes)[number]["value"];
export const TripType = memo(function TripTypeSelector({
  form,
}: TripTypeSelectorProps) {
  const handleTripTypeChange = useCallback(
    (
      field: ControllerRenderProps<TFlightSearchFormData, "tripType">,
      newTripType: TripTypeValue,
    ) => {
      field.onChange(newTripType);

      const currentFlights = form.getValues("flights");
      const updatedFlights = FlightFormHelper.updateFlightsOnTripTypeChange(
        newTripType,
        currentFlights,
      );

      form.setValue("flights", updatedFlights);

      // clear date errors
      updatedFlights.forEach((_, index) => {
        form.clearErrors(`flights.${index}.departDate`);
      });
    },
    [form],
  );

  return (
    <Controller
      control={form.control}
      name="tripType"
      render={({ field }) => (
        <Field className="min-w-0 flex-1 md:w-fit md:flex-none">
          <div className="grid min-w-0 grid-cols-3 gap-1 md:flex md:w-fit md:gap-2">
            {tripTypes.map((type) => {
              const isActive = field.value === type.value;
              const mobileLabel =
                type.value === "oneway"
                  ? "One"
                  : type.value === "roundtrip"
                    ? "Round"
                    : "Multi";

              return (
                <Button
                  key={type.value}
                  type="button"
                  variant={isActive ? "default" : "outline"}
                  onClick={() => handleTripTypeChange(field, type.value)}
                >
                  <span
                    className={cn(
                      "relative hidden size-4 rounded-full border md:block",

                      isActive &&
                        "md:border-primary-foreground md:before:absolute md:before:left-1/2 md:before:top-1/2 md:before:block md:before:size-2 md:before:-translate-x-1/2 md:before:-translate-y-1/2 md:before:rounded-full md:before:bg-primary-foreground md:before:content-['']",
                      !isActive && "border-muted-foreground/50",
                    )}
                  />

                  <span className="truncate md:hidden">{mobileLabel}</span>
                  <span className="hidden truncate md:inline">
                    {type.label}
                  </span>
                </Button>
              );
            })}
          </div>
        </Field>
      )}
    />
  );
});

export const FlightFields = {
  Airport,
  Date,
  CabinClass,
  Passenger,
  TripType,
};
